// fbspeedlag (owner bug 2026-10-21, FT3D: "took multiple times to switch preferred Speed Type to teleport"): the switch is the page's
// decision, so every surface a drag reads takes it AT ONCE. Before this, only the token's DOM dataset changed (after the save); the parent's
// held encounter row (window.ftMovementSpeedVariable — the speed every drag commit sends) kept the old speed until the recompile relay
// landed, measured 26.7 s after the click (8.4 s of it the save itself), so drags in that window still walked.
import { encounterCreatureField } from '../shared/array-types';

type Row = Record<string, unknown>;
/** One surface a drag reads the preference from; `null` = the field absent. */
export interface SpeedTarget { get(): unknown; set(value: string | null): void }
interface SpeedDocument {
  getElementById(id: string): { dataset: DOMStringMap } | null;
  querySelectorAll(selector: string): Iterable<{ dataset: DOMStringMap }>;
}
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const upper = (value: unknown): string => String((Array.isArray(value) ? value[0] : value) ?? '').trim().toUpperCase();

/** The surfaces, re-read on every write (a relay replaces rows): the held encounter row, the caster card's row (one object counted once),
 *  and the token elements DragManager / TokenDragController read before any row. */
export function preferredSpeedTargets(encounter: Row | null, casterRow: unknown, actorId: string, doc: SpeedDocument | null): SpeedTarget[] {
  const wanted = upper(actorId);
  const rows: Row[] = [];
  for (const row of [...(Array.isArray(encounter?.encounter_creatures_arr) ? encounter.encounter_creatures_arr : []), casterRow]) {
    const ec = record(record(row)?.encounter_creatures);
    if (ec && !rows.includes(ec) && upper(encounterCreatureField(ec, 'encounter_creature_id')) === wanted) rows.push(ec);
  }
  const elements: Array<{ dataset: DOMStringMap }> = [];
  const byId = doc?.getElementById(`creature_${actorId}`);
  for (const el of [...(byId ? [byId] : []), ...(doc ? doc.querySelectorAll(`.encounter-creature[data-draggable_id="${actorId}"], .gm-encounter-creature-card[data-draggable_id="${actorId}"]`) : [])])
    if (!elements.includes(el)) elements.push(el);
  return [
    ...rows.map(ec => ({ get: () => ec.preferred_speed_type, set: (v: string | null) => { if (v === null) delete ec.preferred_speed_type; else ec.preferred_speed_type = v; } })),
    ...elements.map(el => ({ get: () => el.dataset.preferred_speed_type, set: (v: string | null) => { if (v === null) delete el.dataset.preferred_speed_type; else el.dataset.preferred_speed_type = v; } })),
  ];
}

/** One actor's switches in flight: the newest of {confirmed, pending} by order of the clicks is what the surfaces show.
 *  fbspeedlag2 (owner 2026-10-21): the switch is HELD against base paints from before it. A paint announces its base revision; one at or
 *  below `rev` (the newest revision the page had heard of at the latest click) cannot carry the switch, so whatever it
 *  repainted is put back. Staging: a compile of the PRE-save revision repainted walking 3 ms after the 200 and the save's own revision painted
 *  ~11 s later (base-runtime persistTouched no longer repaints, but a compile already in flight at the click still lands after it).
 *  With no save of the slot still unanswered, the first paint ABOVE `rev` is truth past the switch — the switch itself, or someone's newer
 *  change — and ends the hold. While one is unanswered its own revision is still to come and newer than anything painted, so the hold stays
 *  (an earlier click's save painting first must not replace the latest click). HOLD_MS after the latest click or answer ends it regardless.
 *  A page with no revisions (no base runtime) is not held: there is nothing to order a paint by. */
interface Slot {
  seq: number; confirmed: { seq: number; value: string | null }; pending: Map<number, string>; shown: string | null;
  targets: () => SpeedTarget[]; entity: string; rev: number | undefined; until: number;
}
export const PREFERRED_SPEED_HOLD_MS = 45_000;
const slots = new Map<string, Slot>();
const speedId = (value: unknown): string | null => {
  const id = encounterCreatureField({ preferred_speed_type: value }, 'preferred_speed_type');
  return id === undefined || id === null || id === '' ? null : String(id);
};
const now = (): number => { const clock = (globalThis as { performance?: { now(): number } }).performance; return clock ? clock.now() : Date.now(); };
type Host = { addEventListener?: (type: string, fn: (event: Event) => void) => void; ftBaseHeardRevision?: (entityId: string) => number | undefined };
const host = (): Host => globalThis as unknown as Host;
/** The revision each entity's surfaces were last painted from (`ft:entity-base-compiled`). */
const painted = new Map<string, number>();
/** A paint that cannot carry the switch is undone; the first one past the click (or the bound) releases the slot. */
function repair(s: Slot): boolean {
  if (now() > s.until) return true;
  if (s.rev === undefined) return !s.pending.size; // nothing to order a paint by: the slot only orders this page's own saves
  const current = painted.get(s.entity);
  if (!s.pending.size && current !== undefined && current > s.rev) return true;
  for (const target of s.targets()) if (speedId(target.get()) !== s.shown) target.set(s.shown);
  return false;
}
let watching: ReturnType<typeof setInterval> | null = null;
function repairAll(): void {
  for (const [key, s] of slots) if (repair(s)) slots.delete(key);
  if (!slots.size && watching !== null) { clearInterval(watching); watching = null; }
}
const REPAINTS = ['ft:entity-base-compiled', 'ft:inventory-base-compiled', 'ft:encounter-state-received', 'ft:ws-state-frame'];
let listening = false;
function listen(): void {
  const page = host();
  if (listening || typeof page.addEventListener !== 'function') return;
  listening = true;
  page.addEventListener('ft:entity-base-compiled', event => {
    const detail = (event as CustomEvent<{ entityId?: unknown; baseRev?: unknown }>).detail;
    if (typeof detail?.baseRev === 'number') painted.set(String(detail.entityId ?? '').toUpperCase(), detail.baseRev);
  });
  // A repaint fires these after it replaces the rows; repair now and once more after the dispatching task (a later listener may paint).
  for (const type of REPAINTS) page.addEventListener(type, () => { if (slots.size) { repairAll(); setTimeout(repairAll, 0); } });
}
listen();
function watch(): void {
  listen();
  if (watching === null) {
    watching = setInterval(repairAll, 250); // any other writer (a WS delta, a caster refetch) within a quarter second
    (watching as { unref?: () => void }).unref?.();
  }
}
/** The newest revision the page has heard of for `entity`, never below what the slot already recorded. */
function heard(entity: string, floor?: number): number | undefined {
  const revisions = [host().ftBaseHeardRevision?.(entity), painted.get(entity), floor].filter((rev): rev is number => typeof rev === 'number');
  return revisions.length ? Math.max(...revisions) : undefined;
}

/** Show `selection` on every surface now; the returned settle answers its save. A refused save falls back to the newest selection still
 *  standing (a later pending click, else the last saved one, else the value before the first click); a save completing out of order never
 *  republishes an older click. A settle only rewrites surfaces still showing this controller's value, so a relay is never clobbered.
 *  `key` is `<encounter id>|<actor id>`: the encounter's paints order the hold. */
export function switchPreferredSpeed(key: string, targets: () => SpeedTarget[], selection: string): (outcome: 'saved' | 'refused') => void {
  let slot = slots.get(key);
  if (slot && repair(slot)) { slots.delete(key); slot = undefined; }
  if (!slot) {
    const first = targets()[0];
    const before = first ? speedId(first.get()) : null;
    slot = { seq: 0, confirmed: { seq: 0, value: before }, pending: new Map(), shown: before, targets, entity: key.split('|')[0]!.toUpperCase(), rev: undefined, until: 0 };
    slots.set(key, slot);
  }
  const s = slot, seq = ++s.seq;
  s.targets = targets;
  s.pending.set(seq, selection);
  s.rev = heard(s.entity, s.rev);
  s.until = now() + PREFERRED_SPEED_HOLD_MS;
  for (const target of targets()) target.set(selection);
  s.shown = selection;
  watch();
  let settled = false;
  return outcome => {
    if (settled) return;
    settled = true;
    // A released slot answers to nothing: newer truth (or a later click's slot) owns the surfaces now.
    if (slots.get(key) !== s) return;
    s.pending.delete(seq);
    if (outcome === 'saved' && seq > s.confirmed.seq) s.confirmed = { seq, value: selection };
    let want = s.confirmed;
    for (const [at, value] of s.pending) if (at > want.seq) want = { seq: at, value };
    if (want.value !== s.shown) {
      for (const target of s.targets()) if (speedId(target.get()) === s.shown) target.set(want.value);
      s.shown = want.value;
    }
    // Nothing saved beyond what the page showed before the first click: there is no newer truth to wait for.
    if (!s.pending.size && s.confirmed.seq === 0) { slots.delete(key); return; }
    s.until = now() + PREFERRED_SPEED_HOLD_MS;
    repairAll();
  };
}

/** The save refused the switch: no answer (an HTTP / network failure, or the request never left), or the endpoint's refusal shape — its
 *  display_warning() block and nothing else (every refusal there warns, then exits before the write). */
export function preferredSpeedSaveRefused(answer: string | null): boolean {
  if (answer === null) return true;
  const text = answer.trim();
  return /^<h5 class="bg-warning(?: [^"]*)?">/.test(text) && text.endsWith('</h5>') && text.split('<h5').length === 2;
}
